import React, { useEffect, useMemo, useRef, useState } from 'react';
import Icon from '../shared/Icon';
import PanelHeader from '../shared/PanelHeader';
import StageBar from './workspace/StageBar';
import MontagemSection from './workspace/MontagemSection';
import DocumentosSection from './workspace/DocumentosSection';
import EnvioSection from './workspace/EnvioSection';
import DisputaResultadoSection from './workspace/DisputaResultadoSection';
import AtividadePanel from './workspace/AtividadePanel';
import { UseProposalWorkspaceResult } from '../../hooks/useProposalWorkspace';
import { derivePrimaryAction, isProrrogadoSemData, PrimaryActionId, ProposalStage, STAGE_LABELS } from '../../utils/proposalStage';
import { proposalStatusBadge, TONE_BADGE } from '../../utils/statusColors';
import { getOrgaoDisplay, formatNumeroCompraComAno, fmtDateTime, getStructureBadge, getModoDisputaBadge } from '../../utils/formatters';
import { useConfirm } from '../shared/ConfirmDialog';
import type { Carga } from '../../design';
import { falhaIgnoradaDeProposito } from '../../design';
import { procurementService } from '../../services/api';
import { envioPeloHub, lerRegistro } from './portais/capacidades';
import { useGravacaoNoPortal } from '../../hooks/useGravacaoNoPortal';
import { envioRecusadoPeloPortal, jobTerminou, placarDaGravacao, propostaEnviadaPeloHub } from '../../utils/gravacaoNoPortal';
import { opcoesDaConfirmacaoDoEnvio } from './workspace/GravarItensNoPortal';
import type { PortalDoRegistro } from './portais/capacidades';
import type { JobDaLista } from '../../utils/jobDeAutomacao';

/**
 * PÁGINA DE TRABALHO da proposta.
 *
 * Substitui a pilha de drawers (detalhe por cima da lista + editor por cima do
 * detalhe) por uma página só, organizada pelo CICLO DE VIDA da proposta. O
 * problema que ela resolve, nas palavras do dono do produto: "uma coisa é
 * termos, outra é montar a proposta" e "hoje são muitos botões" — o drawer
 * antigo tinha 10 ações de peso idêntico e nenhuma noção de progresso.
 *
 * Regras de desenho (docs/ui-guidelines.md §3b):
 *  - UMA ação primária laranja, derivada do estágio que está sendo visto;
 *  - todo o resto vai pro menu "⋯";
 *  - cor de status só via statusColors.
 */

export interface WorkspaceActions {
  onBack: () => void;
  onLoadItems: () => void;
  onOpenSubmit: () => void;
  onWithdraw: () => void;
  onViewJob: (job: any) => void;
  onOpenAi: () => void;
  onOpenPortal: () => void;
  onSyncPncp: () => void;
  onToggleMonitored: (v: boolean) => void;
  onMarkResult: (status: string) => void;
  /** Portal sem integração de envio: registra o cadastro feito à mão. */
  onMarkSent: () => void;
  onArchive: () => void;
  onDelete: () => void;
  onCreateDispute: () => void;
  onOpenDispute: () => void;
  onAdequar: () => void;
  onOpenGeneratePanel: () => void;
  showToast: (msg: string, type?: 'success' | 'error') => void;
}

interface Props {
  ws: UseProposalWorkspaceResult;
  actions: WorkspaceActions;
  /** Da LISTA de jobs (formato enxuto): ver utils/jobDeAutomacao. */
  latestJob?: JobDaLista | null;
  activeOp?: { label: string; currentStep: string; steps: string[]; elapsedMs: number } | null;
  fmt: (v: any) => string;
  catserOptions: { code: string; label: string }[];
  templates: any[];
  jobs?: JobDaLista[];
  /** Estado da carga do histórico de envios; ver EnvioSection. */
  historicoDeEnvio?: Pick<Carga, 'estado' | 'erro'> | null;
  onOpenSignature: (doc?: any, opts?: { replace?: boolean }) => void;
}

export default function ProposalWorkspace({
  ws, actions, latestJob, activeOp, fmt, catserOptions,
  templates, jobs, historicoDeEnvio, onOpenSignature,
}: Props) {
  const { proposal, item, stage, viewStage, setViewStage, dirty, saving, total } = ws;
  const confirm = useConfirm(); // §8: confirmação padrão do HUB, não o nativo
  const [menuOpen, setMenuOpen] = useState(false);
  const [abrirDesfecho, setAbrirDesfecho] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);

  // countdown vivo do encerramento (tick de 1s só quando falta < 24h)
  const [nowTick, setNowTick] = useState(() => Date.now());
  const encTs = item?.dataEncerramento ? new Date(item.dataEncerramento).getTime() : null;
  const faltaMs = encTs ? encTs - nowTick : null;
  const precisaTick = faltaMs != null && faltaMs > 0 && faltaMs < 24 * 3600_000;
  useEffect(() => {
    if (!precisaTick) return;
    const t = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(t);
  }, [precisaTick]);

  useEffect(() => {
    if (!menuOpen) return;
    const close = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [menuOpen]);

  // O portal tem envio pelo HUB? A MESMA fonte da matriz de portais (a etapa `cadastrar`), nunca uma lista aqui.
  // Sem a carga, 'desconhecido': a tela fica como era, com o botão, e o backend recusa o que não é dele.
  const [registroDosPortais, setRegistroDosPortais] = useState<PortalDoRegistro[] | null>(null);
  useEffect(() => {
    let vivo = true;
    procurementService.getCapacidadesDosPortais()
      .then((r: any) => { if (vivo) setRegistroDosPortais(lerRegistro(r)); })
      .catch(falhaIgnoradaDeProposito('sem o registro, o botão de envio fica como era e o backend recusa o portal que não é dele'));
    return () => { vivo = false; };
  }, []);
  const envio = envioPeloHub(registroDosPortais, item?.portalOrigem);
  // Portal que GRAVA ITENS e para (o modo vem do registro, nunca do nome do portal): a prévia, o pedido e o job. Fica
  // aqui porque o botão do topo e o da etapa Envio são a mesma ação.
  const gravacao = useGravacaoNoPortal(proposal?.id, envio === 'grava_itens', ws.salvarMarcaDoItem, {
    // Proposta que não existe no portal: o envio pergunta antes (os dois botões passam por aqui) e, com o sim do PORTAL,
    // relê a lista, que traz a proposta como enviada.
    confirmar: (previa) => confirm(opcoesDaConfirmacaoDoEnvio(previa)),
    aoEnviar: () => ws.reload(),
  });
  const placarDoTopo = jobTerminou(gravacao.job) && gravacao.job?.resultado
    ? placarDaGravacao(gravacao.job, gravacao.previa?.linhas.length ?? 0) : null;
  const estadoDaGravacao = envio === 'grava_itens' ? {
    podeGravar: gravacao.podeGravar,
    gravando: gravacao.gravando,
    gravada: !!placarDoTopo,
    comRecusado: !!placarDoTopo && (placarDoTopo.recusados > 0 || placarDoTopo.semResposta > 0),
    exigeConfirmacao: gravacao.previa?.exigeConfirmacao === true,
    enviada: propostaEnviadaPeloHub(gravacao.job),
    // Envio recusado pelo portal, ou de destino incerto: o topo não oferece mandar de novo (ver GravarItensNoPortal).
    semNovoEnvio: gravacao.previa?.exigeConfirmacao === true && !gravacao.gravando
      && (envioRecusadoPeloPortal(gravacao.job) || !!gravacao.erro),
  } : null;

  const primary = useMemo(() => derivePrimaryAction(viewStage, stage, {
    proposal,
    item,
    latestJob,
    dirty,
    hasItems: ws.items.length > 0,
    docsCheck: ws.docsCheck,
    envioPeloHub: envio,
    gravacao: estadoDaGravacao,
  }), [viewStage, stage, proposal, item, latestJob, dirty, ws.items.length, ws.docsCheck, envio,
    estadoDaGravacao?.podeGravar, estadoDaGravacao?.gravando, estadoDaGravacao?.gravada, estadoDaGravacao?.comRecusado,
    estadoDaGravacao?.exigeConfirmacao, estadoDaGravacao?.enviada, estadoDaGravacao?.semNovoEnvio]); // eslint-disable-line react-hooks/exhaustive-deps

  const runPrimary = async (id: PrimaryActionId) => {
    switch (id) {
      case 'salvar': await ws.save(); break;
      case 'carregar-itens':
      case 'sincronizar': actions.onLoadItems(); break;
      case 'ir-documentos': setViewStage('documentos'); break;
      case 'gerar-documentos':
        // a própria seção Documentos tem o painel de geração
        setViewStage('documentos');
        break;
      case 'enviar':
      case 'reenviar':
        if (dirty) await ws.save({ silent: true });
        actions.onOpenSubmit();
        break;
      case 'marcar-enviada': actions.onMarkSent(); break;
      case 'gravar-itens':
        if (dirty) await ws.save({ silent: true });
        await gravacao.gravar();
        break;
      case 'acompanhar-envio': if (latestJob) actions.onViewJob(latestJob); break;
      case 'criar-disputa': actions.onCreateDispute(); break;
      case 'abrir-disputa': actions.onOpenDispute(); break;
      case 'adequar': actions.onAdequar(); break;
      // ⛔ Isto abria o MENU. Fazia sentido enquanto o menu tinha
      // Ganhamos/Perdemos — mas eles viraram DESFECHO, coluna própria, e o
      // menu ficou só com o encerramento do acompanhamento. O botão passou a
      // prometer uma coisa e entregar outra.
      // Agora abre o seletor de desfecho, que é o que "marcar resultado" quer
      // dizer. Se o desfecho já existe, o próprio seletor mostra o valor e a
      // opção de alterar.
      case 'marcar-resultado': setAbrirDesfecho(true); break;
    }
  };

  const handleBack = async () => {
    if (dirty && !(await confirm({
      title: 'Sair sem salvar?',
      message: 'Há alterações não salvas nesta proposta.',
      consequence: 'As alterações feitas desde o último salvamento serão perdidas.',
      danger: true,
      confirmLabel: 'Sair sem salvar',
      cancelLabel: 'Continuar editando',
    }))) return;
    actions.onBack();
  };

  if (!proposal) {
    return (
      <div className="space-y-6">
        <button onClick={actions.onBack} className="flex items-center gap-1 text-sm text-slate-400 hover:text-white">
          <Icon name="chevronLeft" className="w-4 h-4" /> Voltar
        </button>
        <div className="text-slate-400 text-sm animate-pulse">Carregando proposta…</div>
      </div>
    );
  }

  const statusBadge = proposalStatusBadge(proposal.status);
  const portalLabel = item?.portalOrigem ? String(item.portalOrigem).toUpperCase() : null;
  const enviada = proposal.status === 'enviada';

  // Modalidade no header: Pregão e Dispensa têm ritos e telas de disputa
  // diferentes, então saber qual é vale antes de qualquer decisão da página.
  // Mesmo vocabulário/cores da lista (PE azul / DE roxo). ⚠️ modalidadeId não é
  // confiável — a leitura vem de modalidadeNome (ver formatters.resolveModalidade).
  const modChip = (() => {
    const nome = String(item?.modalidadeNome || '');
    if (!nome) return null;
    if (/preg/i.test(nome)) return { short: 'PE', label: 'Pregão', cls: 'bg-blue-500/15 text-blue-300 border-blue-700/40' };
    if (/dispens/i.test(nome)) return { short: 'DE', label: 'Dispensa', cls: 'bg-purple-500/15 text-purple-300 border-purple-700/40' };
    if (/concorr/i.test(nome)) return { short: 'CC', label: 'Concorrência', cls: 'bg-slate-500/15 text-slate-300 border-central-borda' };
    return { short: '', label: nome.split(' ')[0], cls: 'bg-slate-500/15 text-slate-300 border-central-borda' };
  })();
  const structBadge = getStructureBadge(proposal as any);
  const modoBadge = getModoDisputaBadge(item?.modoDisputa);

  const adiadaSemData = isProrrogadoSemData(item);

  const fmtCountdown = () => {
    if (faltaMs == null) return null;
    // O prazo vencido aqui é o VELHO — o portal adiou por aviso e não publicou
    // o novo. Dizer "encerrada" mandaria fechar uma licitação que segue viva.
    if (adiadaSemData) return { txt: 'adiada sem data nova', cls: 'text-amber-400 font-semibold' };
    if (faltaMs <= 0) return { txt: 'encerrada', cls: 'text-slate-400' };
    const h = Math.floor(faltaMs / 3600_000);
    const m = Math.floor((faltaMs % 3600_000) / 60_000);
    if (h < 1) return { txt: `encerra em ${m}min`, cls: 'text-red-400 font-bold' };
    if (h < 24) return { txt: `encerra em ${h}h${String(m).padStart(2, '0')}`, cls: 'text-amber-400 font-semibold' };
    const d = Math.floor(h / 24);
    return { txt: `encerra em ${d}d`, cls: 'text-slate-400' };
  };
  const countdown = fmtCountdown();

  const menuItem = (label: string, onClick: () => void, danger = false) => (
    <button
      key={label}
      onClick={() => { setMenuOpen(false); onClick(); }}
      className={`w-full text-left px-3 py-1.5 text-xs transition-colors ${danger ? 'text-red-400 hover:bg-red-500/10' : 'text-slate-300 hover:bg-central-elevado'}`}
    >{label}</button>
  );

  return (
    <div className="space-y-4">
      {/* ============ HEADER FIXO ============ */}
      {/* 12/10/2026 (o D1 da #470): a proposta rola no <main>, que tem padding-top de 24px; com top-0 o cabeçalho grudava
          24px abaixo da borda e o conteúdo rolado aparecia ACIMA dele. -top-6 desconta o padding. */}
      <div className="sticky -top-6 z-30 -mx-4 px-4 pt-1 pb-0 bg-central-fundo/95 backdrop-blur border-b border-central-borda">
        {/* Mesmo cabeçalho do painel do Encontrar e do drawer de criar
            proposta (§8 do guia). Aqui ele ganha o voltar e a barra de
            estágios logo abaixo — o container muda, a cabeça não. */}
        <PanelHeader
          onBack={handleBack}
          backLabel="Propostas"
          title={getOrgaoDisplay(item) || proposal.title || 'Proposta'}
          titleTooltip={getOrgaoDisplay(item)}
          badges={<>
            {/* M5 (21/10/2026): o prazo vem primeiro, com data e contagem juntas. Ficava no fim da fila, depois do
                status, da modalidade, do portal e do número, em cinza. A cor segue o fmtCountdown. */}
            {(countdown || item?.dataEncerramento) && (
              <span
                className={`px-1.5 py-0.5 rounded border border-central-borda ${countdown?.cls || 'text-slate-400'}`}
                title={adiadaSemData ? 'Prazo ANTIGO — vale só como referência até o órgão remarcar' : 'Encerramento do recebimento de propostas'}
                data-prazo-da-proposta
              >
                {adiadaSemData
                  ? `adiada sem data nova${item?.dataEncerramento ? ` (antes ${fmtDateTime(item.dataEncerramento)})` : ''}`
                  : countdown?.txt === 'encerrada'
                    ? `Encerrou ${item?.dataEncerramento ? fmtDateTime(item.dataEncerramento) : ''}`.trim()
                    : [item?.dataEncerramento ? `Encerra ${fmtDateTime(item.dataEncerramento)}` : null,
                       countdown ? countdown.txt.replace(/^encerra /, '') : null].filter(Boolean).join(' · ')}
              </span>
            )}
            <span className={`px-2 py-0.5 rounded-full ${statusBadge.color}`}>{statusBadge.label}</span>
            {modChip && (
              <span className={`px-1.5 py-0.5 rounded border font-medium ${modChip.cls}`} title={item?.modalidadeNome}>
                {modChip.short ? `${modChip.short} ` : ''}{modChip.label}
              </span>
            )}
            {item?.srp && (
              <span className="px-1.5 py-0.5 rounded border border-central-borda bg-slate-500/15 text-slate-300 font-medium" title="Sistema de Registro de Preços">SRP</span>
            )}
            {structBadge && (
              <span className={`px-1.5 py-0.5 rounded border font-medium ${structBadge.color}`} title={structBadge.full}>{structBadge.short}</span>
            )}
            {modoBadge && (
              <span className={`px-1.5 py-0.5 rounded border border-transparent font-medium ${modoBadge.color}`} title={`Modo de disputa: ${modoBadge.full}`}>{modoBadge.short}</span>
            )}
            {portalLabel && <span className="text-slate-500">{portalLabel}</span>}
            {item?.numeroCompra && (
              <span>nº {formatNumeroCompraComAno(item.numeroCompra, item.anoCompra)}</span>
            )}
          </>}
          metric={{ label: 'Total da proposta', value: fmt(total) }}
          primary={{
            label: saving ? 'Salvando…' : activeOp ? `${activeOp.label}…` : primary.label,
            onClick: () => runPrimary(primary.id),
            disabled: primary.disabled || saving || !!activeOp,
            title: primary.reason,
          }}
          secondary={dirty ? (
            <button
              onClick={() => ws.save()}
              disabled={saving}
              className="text-[11px] px-2 py-1 rounded bg-amber-500/20 text-amber-300 hover:bg-amber-500/30 transition-colors"
              title="Há alterações não salvas"
            >● não salvo</button>
          ) : undefined}
          menuItems={[
            { label: '🔄 Sincronizar do portal', onClick: actions.onLoadItems },
            { label: item?.aiAnalysisAt ? '⚡ Ver Análise IA' : '⚡ Analisar edital com IA', onClick: actions.onOpenAi },
            { label: '🏛️ Abrir no portal', onClick: actions.onOpenPortal },
            { label: '🔎 Buscar dados no PNCP', onClick: actions.onSyncPncp },
            ...(latestJob ? [{ label: '📋 Ver último job', onClick: () => actions.onViewJob(latestJob) }] : []),
            { label: item?.monitored ? '🔕 Desativar monitoramento' : '🔔 Monitorar mensagens', onClick: () => actions.onToggleMonitored(!item?.monitored) },
            ...(enviada ? [] : [{ label: '📤 Marcar como enviada (envio manual)', onClick: actions.onMarkSent }]),
            // Ganhamos/Perdemos saíram daqui: viraram DESFECHO, coluna própria,
            // e se registram no estágio Resultado. Este menu mexe no ciclo de
            // vida do processo, que é outra pergunta. Ver D-III.
            { label: 'Encerrar acompanhamento', section: true, onClick: () => {} },
            { label: '📋 Encerrada (sem ganhar)', onClick: () => actions.onMarkResult('encerrada') },
            { label: '🚪 Desistimos', onClick: () => actions.onMarkResult('desistida') },
            ...(enviada ? [{ label: '🗑️ Desistir da compra no portal', onClick: actions.onWithdraw, danger: true }] : []),
            { label: proposal.archived ? '📂 Desarquivar' : '📁 Arquivar', onClick: actions.onArchive },
            { label: 'Excluir proposta', onClick: actions.onDelete, danger: true },
          ]}
          className="border-b-0 px-0"
        />

        {/* ============ BARRA DE ESTÁGIOS ============ */}
        <StageBar stage={stage} viewStage={viewStage} onSelect={setViewStage} />
      </div>

      {/* ============ AVISOS ============ */}
      {adiadaSemData && (
        <div className="px-3 py-2 rounded-lg border border-amber-600/50 bg-amber-500/5 text-[11px] text-amber-300">
          ⏸️ <b>Sessão adiada sem data nova.</b> O prazo mostrado acima é o antigo. O
          portal comunicou o adiamento só por aviso de texto, sem registrar o evento
          no sistema, então nada de estruturado mudou lá. A licitação continua em
          <b> Ativas</b> e o robô detecta o novo prazo sozinho quando o órgão publicar.
          {item?.situacaoCompra?.ultimoAviso?.mensagem && (
            <div className="mt-1 text-amber-200/70 italic">
              “{String(item.situacaoCompra.ultimoAviso.mensagem).trim().slice(0, 400)}”
            </div>
          )}
        </div>
      )}
      {enviada && (
        <div className="px-3 py-2 rounded-lg border border-amber-600/50 bg-amber-500/5 text-[11px] text-amber-300">
          ⚠️ Proposta já <b>ENVIADA</b> ao portal: alterar valores aqui <b>não atualiza o portal</b> — o que vale lá é o que foi registrado no envio. Pra corrigir, use Desistir e reenvie.
        </div>
      )}
      {stage.blocked && (
        <div className={`px-3 py-2 rounded-lg border text-[11px] ${TONE_BADGE.warning} border-amber-600/50`}>
          🚧 <b>{stage.blocked.reason}</b>{stage.blocked.detail ? ` — ${stage.blocked.detail}` : ''}
        </div>
      )}

      {/* Operação em andamento: o usuário precisa ver que algo está rodando sem
          abrir painel nenhum (a desistência/envio levam até 2 minutos). */}
      {activeOp && (
        <div className="px-3 py-2 rounded-lg border border-central-borda bg-central-elevado">
          <div className="flex items-center justify-between text-xs text-slate-200 mb-1">
            <span className="flex items-center gap-2">
              <Icon name="refresh" className="w-3.5 h-3.5 animate-spin" />
              {activeOp.label}
            </span>
            <span className="text-slate-400">{Math.round(activeOp.elapsedMs / 1000)}s</span>
          </div>
          <div className="h-1.5 bg-central-fundo rounded overflow-hidden">
            <div
              className="h-full bg-primary transition-all"
              style={{ width: `${Math.min(95, ((activeOp.steps.indexOf(activeOp.currentStep) + 1) / Math.max(1, activeOp.steps.length)) * 100)}%` }}
            />
          </div>
          <div className="text-[10px] text-slate-400 mt-1">{activeOp.currentStep}</div>
        </div>
      )}

      {/* ============ SEÇÃO DO ESTÁGIO ============ */}
      {viewStage === 'montagem' && (
        <MontagemSection ws={ws} actions={actions} fmt={fmt} catserOptions={catserOptions} busy={!!activeOp} />
      )}

      {viewStage === 'documentos' && (
        <DocumentosSection
          ws={ws}
          actions={actions}
          templates={templates}
          fmt={fmt}
          onOpenSignature={onOpenSignature}
        />
      )}

      {viewStage === 'envio' && (
        <EnvioSection ws={ws} actions={actions} latestJob={latestJob} jobs={jobs} historicoDeEnvio={historicoDeEnvio} envioPeloHub={envio} gravacao={gravacao} />
      )}

      {(viewStage === 'disputa' || viewStage === 'resultado') && (
        <DisputaResultadoSection
          ws={ws}
          actions={actions}
          fmt={fmt}
          modo={viewStage}
          abrirDesfecho={abrirDesfecho}
          onDesfechoFechado={() => setAbrirDesfecho(false)}
        />
      )}

      <AtividadePanel
        latestJob={latestJob}
        activeOp={activeOp}
        item={item}
        onViewJob={actions.onViewJob}
        defaultOpen={viewStage === 'disputa' || viewStage === 'resultado'}
        onQuadroRefreshed={(veio, motivo) => {
          if (veio) actions.showToast('Quadro informativo atualizado', 'success');
          else if (motivo === 'sessao_expirada') actions.showToast('Sessão do ComprasNet expirada — use "Renovar sessão"', 'error');
          else if (motivo === 'portal_sem_conteudo') actions.showToast('O portal não devolveu o texto destes registros', 'error');
          else actions.showToast('Falha ao falar com o ComprasNet', 'error');
          ws.reload();
        }}
      />
    </div>
  );
}
